import { useState, useEffect, useCallback } from 'react';
import { 
  fetchPaginatedOfferPages, 
  updateOfferPageStatus,
  type OfferPagesFilters, 
  type PaginatedOfferPagesResponse, 
  type OfferPage 
} from '../services/paginatedOfferPageService.ts';

interface UseServerPaginatedOfferPagesReturn {
  offerPages: OfferPage[];
  loading: boolean;
  error: string | null;
  currentPage: number;
  totalPages: number;
  totalCount: number;
  totalItems: number;
  itemsPerPage: number;
  hasMore: boolean;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  startItem: number;
  endItem: number;

  goToPage: (page: number) => void;
  goToNextPage: () => void;
  goToPreviousPage: () => void;
  goToFirstPage: () => void;
  goToLastPage: () => void;
  nextPage: () => void;
  prevPage: () => void;
  setFilters: (filters: OfferPagesFilters) => void;
  setItemsPerPage: (limit: number) => void;
  archiveOfferPage: (id: string) => Promise<void>;
  activateOfferPage: (id: string) => Promise<void>;
  refreshData: () => void;
  refresh: () => void;
}

export const useServerPaginatedOfferPages = (
  initialLimit: number = 10,
  initialFilters: OfferPagesFilters = {}
): UseServerPaginatedOfferPagesReturn => {
  const [offerPages, setOfferPages] = useState<OfferPage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [filters, setFiltersState] = useState<OfferPagesFilters>(initialFilters);
  const [limit, setLimit] = useState(initialLimit);

  const totalItems = totalCount;
  const itemsPerPage = limit;
  const hasNextPage = hasMore;
  const hasPreviousPage = currentPage > 1;
  const startItem = totalCount > 0 ? (currentPage - 1) * limit + 1 : 0;
  const endItem = Math.min(currentPage * limit, totalCount);

  const fetchData = useCallback(async (page: number = currentPage) => {
    setLoading(true);
    setError(null);
    
    try {
      const response: PaginatedOfferPagesResponse = await fetchPaginatedOfferPages(
        page, 
        limit, 
        filters
      );
      
      setOfferPages(response.offerPages);
      setCurrentPage(response.currentPage);
      setTotalPages(response.totalPages);
      setTotalCount(response.totalCount);
      setHasMore(response.hasMore);
      
    } catch (err) {
      console.error('Error fetching offer pages:', err);
      setError(err instanceof Error ? err.message : 'Failed to fetch offer pages');
      setOfferPages([]);
    } finally {
      setLoading(false);
    }
  }, [limit, filters]); 

  const goToPage = useCallback((page: number) => {
    if (page >= 1 && page <= totalPages && page !== currentPage) {
      setCurrentPage(page);
    }
  }, [totalPages, currentPage]);

  const goToNextPage = useCallback(() => {
    if (hasNextPage) {
      setCurrentPage(prev => prev + 1);
    }
  }, [hasNextPage]);

  const goToPreviousPage = useCallback(() => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  }, [currentPage]);

  const goToFirstPage = useCallback(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  }, [currentPage]);

  const goToLastPage = useCallback(() => {
    if (currentPage !== totalPages && totalPages > 0) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const nextPage = useCallback(() => {
    goToNextPage();
  }, [goToNextPage]);

  const prevPage = useCallback(() => {
    goToPreviousPage();
  }, [goToPreviousPage]);

  const setFilters = useCallback((newFilters: OfferPagesFilters) => {
    setFiltersState(newFilters);
    setCurrentPage(1); 
  }, []);

  const setItemsPerPage = useCallback((newLimit: number) => {
    setLimit(newLimit);
    setCurrentPage(1); 
  }, []);

  const archiveOfferPage = useCallback(async (id: string) => {
    const success = await updateOfferPageStatus(id, 'archived');
    if (success) {
      await fetchData(currentPage);
    }
  }, [fetchData, currentPage]);

  const activateOfferPage = useCallback(async (id: string) => {
    const success = await updateOfferPageStatus(id, 'active');
    if (success) {
      await fetchData(currentPage);
    }
  }, [fetchData, currentPage]);

  const refreshData = useCallback(() => {
    fetchData(currentPage);
  }, [fetchData, currentPage]);

  const refresh = useCallback(() => {
    refreshData();
  }, [refreshData]);

  useEffect(() => {
    setCurrentPage(1); 
    fetchData(1);
  }, [filters, fetchData]);

  useEffect(() => {
    fetchData(currentPage);
  }, [currentPage, fetchData]);

  return {
    offerPages,
    loading,
    error,
    currentPage,
    totalPages,
    totalCount,
    totalItems,
    itemsPerPage,
    hasMore,
    hasNextPage,
    hasPreviousPage,
    startItem,
    endItem,
    goToPage,
    goToNextPage,
    goToPreviousPage,
    goToFirstPage,
    goToLastPage,
    nextPage,
    prevPage,
    setFilters,
    setItemsPerPage,
    archiveOfferPage,
    activateOfferPage,
    refreshData,
    refresh
  };
};

export default useServerPaginatedOfferPages;
